import { useEffect, useState } from 'react';
import type {
  Report,
  ReportCellValue,
  ReportColumn,
  ReportSectionResult,
} from '@catalyst-psa/shared';
import { formatReportDateValue, reportCellHref } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { formatDateTime } from '../../utils/date.js';
import { downloadReportCsv, downloadReportPdf, useRunReport } from './api.js';

interface ReportRunModalProps {
  isOpen: boolean;
  report: Report | null;
  onClose: () => void;
}

const NUMERIC = new Set<ReportColumn['type']>(['number', 'money']);

function formatCell(value: ReportCellValue, type: ReportColumn['type']): string {
  if (value === null || value === undefined) return '';
  if (type === 'money') return typeof value === 'number' ? value.toFixed(2) : String(value);
  if (type === 'boolean') return value ? 'Yes' : 'No';
  if (type === 'date') return formatReportDateValue(String(value));
  return String(value);
}

/** A cell whose value IS a link renders as an anchor (in-app paths stay relative). */
function CellContent({
  value,
  type,
}: {
  value: ReportCellValue;
  type: ReportColumn['type'];
}): JSX.Element {
  const text = formatCell(value, type);
  const href = type === 'string' ? reportCellHref(text) : null;
  if (!href) return <>{text}</>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-brand-600 underline hover:text-brand-700"
    >
      {text}
    </a>
  );
}

function ResultTable({
  columns,
  rows,
}: {
  columns: ReportColumn[];
  rows: Array<Record<string, ReportCellValue>>;
}): JSX.Element {
  return (
    <div className="overflow-auto rounded-lg border border-slate-200">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2 font-medium ${NUMERIC.has(col.type) ? 'text-right' : ''}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={Math.max(1, columns.length)}
                className="px-3 py-8 text-center text-slate-400"
              >
                No rows matched this section.
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={i} className="hover:bg-slate-50">
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-3 py-2 text-slate-700 ${
                      NUMERIC.has(col.type) ? 'text-right tabular-nums' : ''
                    }`}
                  >
                    <CellContent value={row[col.key] ?? null} type={col.type} />
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function SectionBlock({ section }: { section: ReportSectionResult }): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {section.header ? (
        <div
          className="tiptap-content text-sm text-slate-800"
          dangerouslySetInnerHTML={{ __html: section.header }}
        />
      ) : null}
      <p className="text-xs text-slate-500">
        {section.rowCount} row{section.rowCount === 1 ? '' : 's'}
      </p>
      <ResultTable columns={section.columns} rows={section.rows} />
      {section.footer ? (
        <div
          className="tiptap-content text-sm text-slate-800"
          dangerouslySetInnerHTML={{ __html: section.footer }}
        />
      ) : null}
    </div>
  );
}

export function ReportRunModal({ isOpen, report, onClose }: ReportRunModalProps): JSX.Element {
  const runMutation = useRunReport();
  const [downloading, setDownloading] = useState<'csv' | 'pdf' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const reportId = report?.id ?? null;
  const runReset = runMutation.reset;
  const runMutate = runMutation.mutate;

  useEffect(() => {
    if (isOpen && reportId) {
      setDownloadError(null);
      runMutate(reportId);
    } else if (!isOpen) {
      runReset();
    }
  }, [isOpen, reportId, runMutate, runReset]);

  const result = runMutation.data;

  const handleDownload = async (kind: 'csv' | 'pdf'): Promise<void> => {
    if (!reportId) return;
    setDownloading(kind);
    setDownloadError(null);
    try {
      if (kind === 'csv') await downloadReportCsv(reportId);
      else await downloadReportPdf(reportId);
    } catch (error) {
      setDownloadError(
        error instanceof ApiError ? error.message : `Failed to download ${kind.toUpperCase()}.`,
      );
    } finally {
      setDownloading(null);
    }
  };

  const isMultiSection = (result?.sections?.length ?? 0) > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="wide"
      title={report ? `Run report: ${report.name}` : 'Run report'}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => void handleDownload('csv')}
            isLoading={downloading === 'csv'}
            disabled={!result || downloading !== null}
          >
            Download CSV
          </Button>
          <Button
            variant="secondary"
            onClick={() => void handleDownload('pdf')}
            isLoading={downloading === 'pdf'}
            disabled={!result || downloading !== null}
          >
            Download PDF
          </Button>
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {downloadError ? (
        <div className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {downloadError}
        </div>
      ) : null}

      {runMutation.isPending ? (
        <div className="flex items-center justify-center gap-2 py-12 text-slate-500">
          <Spinner className="h-5 w-5" />
          Running reportâ€¦
        </div>
      ) : runMutation.isError ? (
        <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {runMutation.error instanceof ApiError
            ? runMutation.error.message
            : 'Failed to run the report.'}
        </div>
      ) : result ? (
        <div className="flex flex-col gap-6">
          <p className="text-xs text-slate-500">
            {result.rowCount} total row(s) Â· generated {formatDateTime(result.generatedAt)}
          </p>

          {isMultiSection ? (
            result.sections.map((section, idx) => (
              <div key={section.sectionId} className="flex flex-col gap-2">
                {result.sections.length > 1 ? (
                  <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1">
                    Section {idx + 1}
                  </h3>
                ) : null}
                <SectionBlock section={section} />
              </div>
            ))
          ) : (
            <div>
              <ResultTable columns={result.columns} rows={result.rows} />
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
